import uuidv4 from 'uuid/v4';
import { isLocalUser } from '../Helpers/Chat/User';
import {
    GROUP_CHAT_PROFILE_UPDATED_NOTIFY,
    GROUP_DISAPPEAR_UPDATED,
    MSG_PROCESSING_STATUS,
} from '../Helpers/Chat/Constant';
import { markMessageExpired } from '../Helpers/Chat/DisappearScheduler';
import { Store } from '../store';
import { DecreaseUnreadCount } from './UnreadCount';
import { RecentChatUpdateAction } from './RecentChatActions';
import { DISAPPEAR_MESSAGE, REMOVE_STARRED_MESSAGE_BY_MSG_ID } from './Constants';

const NOTIFICATION_MSG_TYPES = new Set([GROUP_CHAT_PROFILE_UPDATED_NOTIFY]);

// A sender's own copy keeps its send state in msgType; the real type is in msgBody.
const getMessageType = message =>
    message.msgType === MSG_PROCESSING_STATUS ? message.msgBody?.message_type || message.msgType : message.msgType;

/**
 * Dispatch all side-effects when a message's disappear_at time is reached:
 *  1. Remove from chatConversationHistory Redux (IndexedDB syncs via idbSyncMiddleware)
 *  2. Clear recent-chat last-message if it was the most recent message
 *  3. Remove from starred messages if starred
 *  4. Decrement unread count if the message was from the other party
 *
 * @param {object} message  - The full message object from Redux
 * @param {string} chatId   - The chat history key (contact userId for 1-to-1, groupId for groups)
 */
export const dispatchDisappearMessage = (message, chatId) => {
    const { msgId, favourite, fromUserId } = message;
    if (!msgId || !chatId) return;

    const state = Store.getState();

    // --- 1. Determine if this is the last message in loaded chat history before removing it
    const chatMessages = state.chatConversationHistory?.data?.[chatId]?.messages || {};
    const sortedMsgs = Object.values(chatMessages).sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0));
    const isLastMsgInHistory = sortedMsgs[0]?.msgId === msgId;

    // --- 2. Also check if this msgId is currently shown as last message in recent chat.
    // This handles the receiver side where the chat is unread (history not loaded in Redux).
    const normalizedChatId = chatId.includes('@') ? chatId.split('@')[0] : chatId;
    const recentChats = state.recentChatData?.data || [];
    const recentEntry = recentChats.find(c => {
        const id = c.fromUserId?.includes('@') ? c.fromUserId.split('@')[0] : c.fromUserId;
        return id === normalizedChatId;
    });
    const isLastMsgInRecent = recentEntry?.msgId === msgId;
    const shouldUpdateRecent = isLastMsgInHistory || isLastMsgInRecent;

    // Compute sender side early — used for both unread count and the synthetic banner fallback.
    const senderId = message.publisherId || fromUserId;
    const isFromOther = !!(senderId && !isLocalUser(senderId));

    // --- 3. Tombstone this msgId so server re-syncs can never resurrect it.
    // The server still holds the message and returns it without disappear_at; without this
    // mark, syncMessageBatch would re-add it from the server, and no new timer would be set.
    markMessageExpired(msgId);

    // Remove from Redux chat history (IDB sync fires via idbSyncMiddleware)
    Store.dispatch({
        type: DISAPPEAR_MESSAGE,
        payload: { id: uuidv4(), data: { msgId, chatId } },
    });

    // --- 4. Update recent-chat last-message entry if this message was shown there.
    // Read the post-removal state so we can show the new last message (not blank).
    if (shouldUpdateRecent) {
        const updatedMessages = Store.getState().chatConversationHistory?.data?.[chatId]?.messages || {};
        const updatedSorted = Object.values(updatedMessages).sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0));

        // Use the most recent item in history, whether it's a real message or a banner.
        // The previous approach (skip notifications, fall back to banner) incorrectly picked
        // older real messages over a newer disappear banner — e.g. after the 3 disappearing
        // messages expire the banner IS the current state and should win over pre-banner messages.
        const effectiveLastMsg = updatedSorted[0];

        if (effectiveLastMsg) {
            const isBanner =
                NOTIFICATION_MSG_TYPES.has(effectiveLastMsg.msgType) || !!effectiveLastMsg.profileUpdatedStatus;
            Store.dispatch(
                RecentChatUpdateAction({
                    filterBy: chatId,
                    msgId: effectiveLastMsg.msgId,
                    msgBody: effectiveLastMsg.msgBody,
                    msgType: getMessageType(effectiveLastMsg),
                    createdAt: effectiveLastMsg.createdAt,
                    // For banners keep the chat's current position in the list — the banner was
                    // created before the messages were sent, so its timestamp is older.
                    timestamp: isBanner ? recentEntry?.timestamp || Date.now() : (effectiveLastMsg.timestamp ?? 0),
                    msgStatus: effectiveLastMsg.msgStatus,
                    profileUpdatedStatus: effectiveLastMsg.profileUpdatedStatus,
                    msgDisappear: effectiveLastMsg.msgDisappear,
                    publisherId: effectiveLastMsg.publisherId,
                    disappearAt: effectiveLastMsg.disappearAt || effectiveLastMsg.disappear_at || null,
                    disappear_at: effectiveLastMsg.disappear_at || effectiveLastMsg.disappearAt || null,
                }),
            );
        } else {
            // No messages in loaded chat history (chat never opened this session, or after
            // page refresh before the timers fired). If disappear is active on this chat,
            // show a synthetic banner so the recent list reflects the current chat state
            // instead of going blank.
            const disappearVal = recentEntry?.msgDisappear;
            if (disappearVal > 0) {
                // publisherId is the person who set the disappear setting:
                //   isFromOther=true  → received messages  → other user set it (= chatId)
                //   isFromOther=false → sent/carbon msgs   → we set it (= own userId)
                const publisherId = isFromOther ? chatId : Store.getState().vCardData?.data?.userId || '';
                Store.dispatch(
                    RecentChatUpdateAction({
                        filterBy: chatId,
                        msgId: '',
                        msgBody: {},
                        msgType: GROUP_CHAT_PROFILE_UPDATED_NOTIFY,
                        profileUpdatedStatus: GROUP_DISAPPEAR_UPDATED,
                        msgDisappear: disappearVal,
                        publisherId,
                        createdAt: '',
                        timestamp: recentEntry?.timestamp || Date.now(),
                        msgStatus: 4,
                        disappearAt: null,
                        disappear_at: null,
                    }),
                );
            } else {
                // No disappear context — preserve the existing timestamp so the chat stays
                // in its correct position in the list (timestamp:0 pushes it to top/bottom).
                Store.dispatch(
                    RecentChatUpdateAction({
                        filterBy: chatId,
                        msgId: '',
                        msgBody: {},
                        msgType: '',
                        createdAt: '',
                        timestamp: recentEntry?.timestamp || Date.now(),
                        msgStatus: 4,
                        disappearAt: null,
                        disappear_at: null,
                    }),
                );
            }
        }
    }

    // --- 5. Remove from starred messages if it was starred
    if (favourite === '1' || favourite === 1) {
        Store.dispatch({
            type: REMOVE_STARRED_MESSAGE_BY_MSG_ID,
            payload: { id: uuidv4(), data: { msgId } },
        });
    }

    // --- 6. Decrement unread count if the message was from the other party.
    // Use publisherId for group messages where fromUserId is the group ID, not the sender.
    // Read fresh state after prior dispatches so concurrent expiries don't double-read stale count.
    if (isFromOther) {
        const currentUnread = Store.getState().unreadCountData?.unreadDataObj?.[chatId]?.count || 0;
        if (currentUnread > 0) {
            Store.dispatch(DecreaseUnreadCount({ fromUserId: chatId }));
        }
    }
};
